import { cn } from "@/lib/utils";

/**
 * KPI Card — Control-Room style. Mono numerical data.
 */
export default function KpiCard({
  label, value, unit, trend, band, sub, testId, icon: Icon, format,
}) {
  const bandClass = bandStyles(band);
  const formatted = formatValue(value, format);
  return (
    <div
      className={cn(
        "group border bg-card p-5 rounded-sm hover-lift relative",
        bandClass.border,
      )}
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <span className="label-mono text-[9px] text-muted-foreground">{label}</span>
        {Icon && <Icon size={14} className="text-muted-foreground" />}
      </div>
      <div className="mt-3 flex items-baseline gap-1.5">
        <span className={cn("font-mono tabular-nums text-3xl font-semibold leading-none", bandClass.text)}>
          {formatted}
        </span>
        {unit && <span className="font-mono text-sm text-muted-foreground">{unit}</span>}
      </div>
      {sub && <div className="mt-2 text-xs text-muted-foreground truncate">{sub}</div>}
      {band && (
        <div className={cn("mt-3 inline-flex label-mono text-[9px] px-2 py-0.5 rounded-sm", bandClass.badge)}>
          {bandLabel(band)}
        </div>
      )}
    </div>
  );
}

function formatValue(v, fmt) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  if (typeof v !== "number") return v;
  if (fmt === "pct") return v.toFixed(1);
  if (fmt === "int") return Math.round(v).toLocaleString("fr-FR");
  if (fmt === "money") {
    if (Math.abs(v) >= 1e9) return (v / 1e9).toFixed(2) + " B";
    if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(2) + " M";
    if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(1) + " K";
    return v.toFixed(0);
  }
  if (fmt === "days") return v.toFixed(0);
  return v.toLocaleString("fr-FR", { maximumFractionDigits: 2 });
}

export function bandStyles(band) {
  switch (band) {
    case "faible":
    case "soutenable":
    case "vert":
      return { border: "border-success/40", text: "text-success", badge: "bg-success/10 text-success" };
    case "modere":
    case "sous_tension":
    case "orange":
      return { border: "border-warn/40", text: "text-warn", badge: "bg-warn/10 text-warn" };
    case "eleve":
    case "critique":
    case "rouge":
      return { border: "border-alert/40", text: "text-alert", badge: "bg-alert/10 text-alert" };
    case "detresse":
    case "insoutenable":
      return { border: "border-[#7A1F15]/60", text: "text-[#7A1F15] dark:text-[#E74C3C]", badge: "bg-[#7A1F15]/10 text-[#7A1F15] dark:text-[#E74C3C]" };
    case "inconnu":
    default:
      return { border: "border-border", text: "text-foreground", badge: "bg-muted text-muted-foreground" };
  }
}

export function bandLabel(band) {
  return {
    faible: "FAIBLE", modere: "MODÉRÉ", eleve: "ÉLEVÉ", detresse: "DÉTRESSE",
    soutenable: "SOUTENABLE", sous_tension: "TENSION", critique: "CRITIQUE", insoutenable: "INSOUTENABLE",
    inconnu: "N/A", vert: "OK", orange: "ATTENTION", rouge: "CRITIQUE",
  }[band] || band?.toUpperCase();
}
